import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister'
import type { Query } from '@tanstack/react-query'
import {
  type Persister,
  type PersistQueryClientOptions,
  removeOldestQuery,
} from '@tanstack/react-query-persist-client'
import { isAuthMeKey } from '@/auth/keys'

/**
 * The server-state cache, kept across reloads (docs/ui-effects.md §16).
 *
 * A reload used to start from nothing: every page waited on its own requests,
 * and the whole app waited on the session check before it drew a thing. The
 * TanStack Query cache is now written to localStorage and put back on load, so
 * the last good answer shows at once and the refetch lands on top of it.
 *
 * What is kept is an ALLOWLIST by shape: only successful queries, never a
 * mutation, and never the keys below — secrets, API keys and the org carry
 * credentials or credential metadata, the Claude accounts hold subscription
 * tokens, jobs holds scraped third-party contacts, reel playback is signed URLs,
 * the R2 connector names the storage credentials, and health is Garmin data. A query can opt out on its own with `meta: { persist: false }`.
 */

export const PERSIST_KEY = 'warike-rq'
export const PERSIST_MAX_AGE = 24 * 60 * 60 * 1000

/** The build id (vite.config.ts `define`); 'dev' where nothing defines it, as under test. */
export const BUILD_ID = typeof __BUILD_ID__ === 'string' ? __BUILD_ID__ : 'dev'

/** Key prefixes that never reach storage. */
const NEVER_PERSIST: readonly (readonly string[])[] = [
  ['auth', 'secrets'],
  ['auth', 'keys'],
  ['auth', 'org'],
  ['claude'],
  // Contacts scraped from third parties: other people's data, never at rest here.
  ['jobs'],
  // Signed URLs: a bearer credential while it lives, and useless once it expires.
  ['reels', 'playback'],
  // The R2 connection: the bucket, the account and the key id that reach it.
  ['auth', 'connectors'],
  // Garmin data: someone's body — sleep, heart rate, weight — never at rest here.
  ['health'],
]

function startsWith(key: readonly unknown[], prefix: readonly string[]): boolean {
  return prefix.every((part, i) => key[i] === part)
}

let signingOut = false

/**
 * Stop writing anything to storage for the rest of this page's life: set as a
 * sign-out starts, so a poll that lands between clearing the cache and leaving
 * the page cannot write the last person's data back. The redirect ends the page.
 */
export function stopPersisting(): void {
  signingOut = true
}

/** Whether one query may be written to storage. */
function shouldPersistQuery(query: Query): boolean {
  if (signingOut) return false
  if (query.state.status !== 'success') return false
  if (query.meta?.persist === false) return false
  if (NEVER_PERSIST.some((prefix) => startsWith(query.queryKey, prefix))) return false
  // A signed-out answer is not worth keeping: the next load should ask, not
  // bounce to /login on a cached "no" while a fresh cookie sits in the jar.
  if (isAuthMeKey(query.queryKey) && query.state.data == null) return false
  return true
}

function browserStorage(): Storage | undefined {
  try {
    return typeof window === 'undefined' ? undefined : window.localStorage
  } catch {
    // Storage blocked (private mode, a sandboxed frame): run without a disk cache.
    return undefined
  }
}

/**
 * The localStorage persister. Without storage it is a no-op, never an error.
 * A full storage drops the oldest query and tries again, so the newest answers
 * still land instead of the last snapshot that fit going stale on disk.
 */
export function createPersister(storage: Storage | undefined = browserStorage()): Persister {
  return createAsyncStoragePersister({
    storage,
    key: PERSIST_KEY,
    throttleTime: 1000,
    retry: removeOldestQuery,
  })
}

/**
 * The persist options for a persister. The buster is the build id, so a deploy
 * throws away a cache whose shapes the new code may not read.
 */
export function persistOptions(
  persister: Persister,
): Omit<PersistQueryClientOptions, 'queryClient'> {
  return {
    persister,
    maxAge: PERSIST_MAX_AGE,
    buster: BUILD_ID,
    dehydrateOptions: {
      shouldDehydrateQuery: shouldPersistQuery,
      shouldDehydrateMutation: () => false,
    },
  }
}

/** The app's one persister. */
export const appPersister = createPersister()
export const appPersistOptions = persistOptions(appPersister)
